/**
 * AI opponents — RULES.md §9 / 卷九.
 *  easy   : uniform random legal move (20% persona-aware greedy fallback)
 *  medium : 1-ply greedy under the persona gain function
 *  hard   : minimax + alpha-beta, iterative deepening under a hard budget
 *
 * Strategy personas (AI_STRATEGIES.md §3, v4.0): difficulty = search effort,
 * strategy = evaluation intent. The two axes are orthogonal — every
 * difficulty honours the seat's persona through a shared gain function:
 *   moveGain(m) = evaluate(after) − evaluate(before)
 *               + chainAppetite × hops(m)                       (per hop)
 *               − bridgeDenial × max(0, oppBestGain(after) − oppBestGain(before))
 *               + bridgeGift   × newTeammateHops(after)
 * Positional terms (opponent/teammate weight) live in evaluate(); chain and
 * bridge terms are move attributes and enter at the candidate/root level.
 *
 * Hard-search design (09-ai/04-difficulty-tuning.md: maxNodes / timeLimitMs /
 * useIterative — the spec these constants implement):
 *  - Iterative deepening with a hard time budget AND a node cap: the search
 *    can never freeze the main thread, no matter how large the branching
 *    factor grows mid-game (the old fixed depth-4 opening search could).
 *  - Iterative deepening is inherently phase-aware: endgames (low branching)
 *    reach deeper plies within the same budget, so the retired — and inverted —
 *    `progress > 0.75 ? 4 : 3` heuristic is no longer needed.
 *  - Moves are ordered by forward gain, which massively improves alpha-beta
 *    pruning and gives a sensible fallback when the budget cuts a depth short.
 *  - The no-moves branch clones before skipping and still consumes a ply, so
 *    the recursion provably terminates even in a full stalemate.
 */
import type { HexPos } from '../types/game.js';
import { hexDistance } from '../utils/hexMath.js';
import { CAMP_POSITIONS, destinationCamp } from '../constants/camps.js';
import { CheckersGame, type SeatTargets } from './rules.js';
import { comboOfPath } from './combo.js';

export type Difficulty = 'easy' | 'medium' | 'hard';

/**
 * Strategy personas (AI_STRATEGIES.md §3): how a seat values the world.
 * Orthogonal to Difficulty, which only controls search effort.
 */
export type AIStrategy = 'balanced' | 'aggressive' | 'defensive' | 'cooperative';

export interface StrategyProfile {
  /** Weight on the LEADING opponent's score in evaluation (classic: 0.6). */
  opponentWeight: number;
  /** Weight on each teammate's score (team formats only; 0 = solo-centric). */
  teammateWeight: number;
  /** Bonus per hop segment of a move — chain appetite (spectacle). */
  chainAppetite: number;
  /** Penalty per unit of opponent best-gain a move concedes (bridge denial). */
  bridgeDenial: number;
  /** Bonus per teammate hop option a move newly enables (bridge gift). */
  bridgeGift: number;
}

/** Persona table (AI_STRATEGIES.md §3). */
export const STRATEGIES: Readonly<Record<AIStrategy, StrategyProfile>> = {
  balanced: {
    opponentWeight: 0.6,
    teammateWeight: 0,
    chainAppetite: 0,
    bridgeDenial: 0,
    bridgeGift: 0,
  },
  aggressive: {
    opponentWeight: 0.15,
    teammateWeight: 0,
    chainAppetite: 2.5,
    bridgeDenial: 0,
    bridgeGift: 0,
  },
  defensive: {
    opponentWeight: 1.2,
    teammateWeight: 0,
    chainAppetite: -0.5,
    bridgeDenial: 6,
    bridgeGift: 0,
  },
  cooperative: {
    opponentWeight: 0.6,
    teammateWeight: 0.5,
    chainAppetite: 0,
    bridgeDenial: 0,
    bridgeGift: 4,
  },
} as const;

export interface AIMove {
  from: HexPos;
  to: HexPos;
}

const WIN_SCORE = 1_000_000;

/** Hard difficulty budget — see 09-ai/04-difficulty-tuning.md. */
const HARD_TIME_MS = 700;
const HARD_MAX_NODES = 200_000;
const HARD_MAX_DEPTH = 8;
/** Clock is sampled every 1024 nodes: cheap, and still a tight deadline. */
const CLOCK_CHECK_MASK = 0x3ff;

/** Internal move candidate carrying its path-derived hop count. */
interface Candidate extends AIMove {
  /** Number of hop segments in the authoritative path (0 for a step). */
  hops: number;
}

/**
 * Resolved persona for one seat in one game: the effective profile plus the
 * seat's teammates. Cooperative without a team degrades to balanced
 * (AI_STRATEGIES.md §3 — graceful degradation).
 */
interface Persona {
  strategy: AIStrategy;
  profile: StrategyProfile;
  /** Teammate seat ids (camp numbers); empty in free-for-all formats. */
  teammates: readonly number[];
}

function resolvePersona(game: CheckersGame, seat: number, strategy: AIStrategy): Persona {
  const team = game.teamOf(seat);
  const effective: AIStrategy = strategy === 'cooperative' && team === null ? 'balanced' : strategy;
  const teammates =
    team === null ? [] : game.seats.filter((s) => s !== seat && game.teamOf(s) === team);
  return { strategy: effective, profile: STRATEGIES[effective], teammates };
}

function candidatesOfSeat(game: CheckersGame, seat: number): Candidate[] {
  const out: Candidate[] = [];
  for (const marble of game.marblesOf(seat)) {
    for (const t of game.reachableTargets(marble)) {
      out.push({ from: marble, to: t.to, hops: comboOfPath(t.path) });
    }
  }
  return out;
}

function allMoves(game: CheckersGame): Candidate[] {
  const out: Candidate[] = [];
  for (const st of game.legalTargets()) {
    for (const t of st.targets) {
      out.push({ from: st.marble, to: t.to, hops: comboOfPath(t.path) });
    }
  }
  return out;
}

/**
 * Legal moves of the on-turn seat, best-first: largest forward gain toward the
 * destination tip first (plus chain appetite, which is positional-noise-free).
 * Ordering drives alpha-beta cut-offs; the same order seeds iterative
 * deepening (previous depth's best line is searched first).
 */
function orderedMoves(game: CheckersGame, persona?: Persona): Candidate[] {
  const seat = game.currentPlayer;
  const tip = CAMP_POSITIONS[destinationCamp(seat)]?.[0];
  const moves = allMoves(game);
  if (!tip) return moves;
  const chain = persona?.profile.chainAppetite ?? 0;
  const gain = (m: Candidate): number =>
    hexDistance(m.from, tip) - hexDistance(m.to, tip) + chain * m.hops;
  return moves.sort((a, b) => gain(b) - gain(a));
}

/** Max achievable progress sum (all marbles at home tip). */
function maxProgress(seat: number): number {
  const homeTip = CAMP_POSITIONS[seat]?.[0];
  const destTip = CAMP_POSITIONS[destinationCamp(seat)]?.[0];
  if (!homeTip || !destTip) return 100;
  return hexDistance(homeTip, destTip) * 10;
}

/** Raw strength of a seat: camp fill dominates, then distance covered. */
function seatScore(game: CheckersGame, seat: number): number {
  return 1000 * game.marblesInDestination(seat) + (maxProgress(seat) - game.progress(seat));
}

/**
 * Positional evaluation from `seat`'s perspective (AI_STRATEGIES.md §6):
 *   evaluate(s) = seatScore(me)
 *               + teammateWeight × Σ teammates seatScore(t)
 *               − opponentWeight × max opponents seatScore(o)
 * Balanced in FFA reduces exactly to the legacy `seatScore − 0.6 × max`.
 */
function evaluatePersona(game: CheckersGame, seat: number, persona: Persona): number {
  if (game.finished) {
    if (game.winner === seat) return WIN_SCORE;
    // Team formats: a teammate clinching the game is our win too (§11.2).
    if (
      game.winnerTeam !== null &&
      game.winner !== null &&
      persona.teammates.includes(game.winner)
    ) {
      return WIN_SCORE;
    }
    return -WIN_SCORE;
  }
  let mine = seatScore(game, seat);
  if (persona.teammates.length > 0 && persona.profile.teammateWeight !== 0) {
    // Shepherding (AI_STRATEGIES.md §3): the least-advanced teammate — lowest
    // seatScore — counts double, so a comfortable seat spends effort on the
    // laggard that actually gates the team's win (RULES §11.2).
    let laggard = -1;
    let laggardScore = Infinity;
    for (const t of persona.teammates) {
      const sc = seatScore(game, t);
      if (sc < laggardScore) {
        laggardScore = sc;
        laggard = t;
      }
    }
    for (const t of persona.teammates) {
      mine += persona.profile.teammateWeight * seatScore(game, t) * (t === laggard ? 2 : 1);
    }
  }
  let theirs = -Infinity;
  for (const s of game.seats) {
    if (s === seat || persona.teammates.includes(s)) continue;
    theirs = Math.max(theirs, seatScore(game, s));
  }
  if (theirs === -Infinity) theirs = 0;
  return mine - persona.profile.opponentWeight * theirs;
}

/** Best single-move forward gain available to any opponent of the persona. */
function bestOpponentGain(game: CheckersGame, seat: number, persona: Persona): number {
  let best = 0;
  for (const s of game.seats) {
    if (s === seat || persona.teammates.includes(s)) continue;
    const tip = CAMP_POSITIONS[destinationCamp(s)]?.[0];
    if (!tip) continue;
    for (const m of candidatesOfSeat(game, s)) {
      best = Math.max(best, hexDistance(m.from, tip) - hexDistance(m.to, tip));
    }
  }
  return best;
}

/** Total hop options currently available to the persona's teammates. */
function teammateHopOptions(game: CheckersGame, teammates: readonly number[]): number {
  let n = 0;
  for (const t of teammates) {
    for (const m of candidatesOfSeat(game, t)) if (m.hops > 0) n++;
  }
  return n;
}

/**
 * Per-turn cache for the gain function: terms of moveGain that do not depend
 * on the candidate move are computed once (AI_STRATEGIES.md §6 note).
 */
interface TurnContext {
  evalBefore: number;
  oppBestBefore: number;
  teammateHopsBefore: number;
}

function makeTurnContext(game: CheckersGame, seat: number, persona: Persona): TurnContext {
  const p = persona.profile;
  return {
    evalBefore: evaluatePersona(game, seat, persona),
    oppBestBefore: p.bridgeDenial > 0 ? bestOpponentGain(game, seat, persona) : 0,
    teammateHopsBefore: p.bridgeGift > 0 ? teammateHopOptions(game, persona.teammates) : 0,
  };
}

/** Static (depth-invariant) part of moveGain for an already-applied position. */
function personaGainAfter(
  game: CheckersGame,
  seat: number,
  persona: Persona,
  ctx: TurnContext,
  hops: number
): number {
  const p = persona.profile;
  let gain = evaluatePersona(game, seat, persona) - ctx.evalBefore;
  if (p.chainAppetite !== 0) gain += p.chainAppetite * hops;
  if (p.bridgeDenial > 0) {
    gain -= p.bridgeDenial * Math.max(0, bestOpponentGain(game, seat, persona) - ctx.oppBestBefore);
  }
  if (p.bridgeGift > 0) {
    gain +=
      p.bridgeGift *
      Math.max(0, teammateHopOptions(game, persona.teammates) - ctx.teammateHopsBefore);
  }
  return gain;
}

/** Shared search state: node counter, hard deadline, abort flag. */
interface SearchState {
  nodes: number;
  deadline: number;
  aborted: boolean;
}

function budgetExceeded(s: SearchState): boolean {
  if (s.nodes >= HARD_MAX_NODES) return true;
  // performance.now() costs ~20ns; sampling every 1024 nodes keeps overhead
  // negligible while bounding overshoot to well under a millisecond.
  return (s.nodes & CLOCK_CHECK_MASK) === 0 && performance.now() > s.deadline;
}

function minimax(
  game: CheckersGame,
  me: number,
  persona: Persona,
  depth: number,
  alpha: number,
  beta: number,
  s: SearchState
): number {
  if (game.finished || depth <= 0) return evaluatePersona(game, me, persona);
  s.nodes++;
  if (budgetExceeded(s)) {
    s.aborted = true;
    return evaluatePersona(game, me, persona);
  }

  const seat = game.currentPlayer;
  const moves = orderedMoves(game, persona);
  if (moves.length === 0) {
    // No legal move: the seat is skipped. Clone first (never mutate a node the
    // caller still iterates over) and consume a ply — both players stalemated
    // must still terminate instead of recursing forever.
    const skipped = game.clone();
    skipped.skipTurn();
    return minimax(skipped, me, persona, depth - 1, alpha, beta, s);
  }

  if (seat === me) {
    let best = -Infinity;
    for (const m of moves) {
      const next = game.clone();
      next.applyMove(m.from, m.to);
      best = Math.max(best, minimax(next, me, persona, depth - 1, alpha, beta, s));
      if (s.aborted) break;
      alpha = Math.max(alpha, best);
      if (alpha >= beta) break;
    }
    return best;
  }
  let worst = Infinity;
  for (const m of moves) {
    const next = game.clone();
    next.applyMove(m.from, m.to);
    worst = Math.min(worst, minimax(next, me, persona, depth - 1, alpha, beta, s));
    if (s.aborted) break;
    beta = Math.min(beta, worst);
    if (beta <= alpha) break;
  }
  return worst;
}

function pickHard(game: CheckersGame, persona: Persona): AIMove | null {
  const me = game.currentPlayer;
  const rootMoves = orderedMoves(game, persona);
  if (rootMoves.length === 0) return null;

  const s: SearchState = {
    nodes: 0,
    deadline: performance.now() + HARD_TIME_MS,
    aborted: false,
  };

  // Chain/bridge terms are move attributes, not positional ones: they enter
  // once per ROOT candidate (depth-invariant), leaving leaf evaluation purely
  // positional so the search budget stays untouched (AI_STRATEGIES.md §5).
  const ctx = makeTurnContext(game, me, persona);
  const staticGain = new Map<Candidate, number>();
  const gainOf = (m: Candidate): number => {
    let g = staticGain.get(m);
    if (g === undefined) {
      const next = game.clone();
      next.applyMove(m.from, m.to);
      // Static terms only: chain appetite + bridge deltas (eval delta is the
      // search's job). Reuse the same formula minus the positional delta.
      const p = persona.profile;
      g = p.chainAppetite * m.hops;
      if (p.bridgeDenial > 0) {
        g -= p.bridgeDenial * Math.max(0, bestOpponentGain(next, me, persona) - ctx.oppBestBefore);
      }
      if (p.bridgeGift > 0) {
        g +=
          p.bridgeGift *
          Math.max(0, teammateHopOptions(next, persona.teammates) - ctx.teammateHopsBefore);
      }
      staticGain.set(m, g);
    }
    return g;
  };

  // Fallback if even depth 1 is cut short: the greedily-best ordered move.
  let completed: { move: Candidate; score: number }[] = [];
  let order = rootMoves;

  for (let depth = 1; depth <= HARD_MAX_DEPTH; depth++) {
    const scored: { move: Candidate; score: number }[] = [];
    for (const m of order) {
      const next = game.clone();
      next.applyMove(m.from, m.to);
      const score = minimax(next, me, persona, depth - 1, -Infinity, Infinity, s) + gainOf(m);
      if (s.aborted) break;
      scored.push({ move: m, score });
    }
    // Only a fully-searched depth may replace the answer — a partial pass has
    // unreliable scores for the unsearched tail of the move list.
    if (s.aborted) break;
    scored.sort((a, b) => b.score - a.score);
    completed = scored;
    order = scored.map((e) => e.move); // best-line-first for the next depth
  }

  if (completed.length === 0) return rootMoves[0] ?? null;

  // Tie pool with a small jitter so repeated positions vary (same spirit as
  // the old `- Math.random()` noise, but confined to near-equal scores).
  const top = completed[0]?.score ?? 0;
  const pool = completed.filter((e) => e.score >= top - 1e-9).map((e) => e.move);
  return pool[Math.floor(Math.random() * pool.length)] ?? null;
}

/**
 * Persona-aware 1-ply greedy (medium; also easy's 20% fallback). Win-clinching
 * moves — solo or team — get a large bonus so the AI never declines a win.
 */
function pickGreedy(game: CheckersGame, persona: Persona, jitter: boolean): AIMove | null {
  const seat = game.currentPlayer;
  const moves = allMoves(game);
  if (moves.length === 0) return null;

  const ctx = makeTurnContext(game, seat, persona);
  let bestGain = -Infinity;
  let best: Candidate[] = [];
  for (const m of moves) {
    const next = game.clone();
    next.applyMove(m.from, m.to);
    let gain = personaGainAfter(next, seat, persona, ctx, m.hops);
    if (next.finished) gain += WIN_SCORE / 2;
    if (jitter) gain += Math.random() * 0.01; // tie-break jitter
    if (gain > bestGain) {
      bestGain = gain;
      best = [m];
    } else if (gain >= bestGain - 1e-9) {
      best.push(m);
    }
  }
  return best[Math.floor(Math.random() * best.length)] ?? null;
}

function pickMedium(game: CheckersGame, persona: Persona): AIMove | null {
  return pickGreedy(game, persona, true);
}

function pickEasy(game: CheckersGame, persona: Persona): AIMove | null {
  const moves = allMoves(game);
  if (moves.length === 0) return null;
  // 20% chance to still take a decent greedy move so easy isn't brain-dead;
  // the fallback carries the same persona (AI_STRATEGIES.md §5).
  if (Math.random() < 0.2) return pickGreedy(game, persona, false);
  return moves[Math.floor(Math.random() * moves.length)] ?? null;
}

/**
 * Pick a move for the on-turn seat. `strategy` defaults to 'balanced', which
 * reproduces the legacy behaviour — all pre-v4.0 call sites stay valid.
 */
export function chooseAIMove(
  game: CheckersGame,
  difficulty: Difficulty,
  strategy: AIStrategy = 'balanced'
): AIMove | null {
  if (game.finished) return null;
  const persona = resolvePersona(game, game.currentPlayer, strategy);
  switch (difficulty) {
    case 'hard':
      return pickHard(game, persona);
    case 'medium':
      return pickMedium(game, persona);
    default:
      return pickEasy(game, persona);
  }
}

/** Exposed for tests / tuning benchmarks. */
export function enumerateMoves(game: CheckersGame): SeatTargets[] {
  return game.legalTargets();
}
